import { FunctionComponent } from "react";

import { Dialog, SubmissionForm } from "@bps/fluent-ui";

import { CreateSecretForm } from "./CreateSecretForm";
import {
  ApplicationDto,
  CloudApplicationSecret
} from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.dtos";
import { useCreateApplicationSecret } from "@libs/api/gateways/plt-integrators/plt-integrators-gateway.hooks";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { Validator } from "@bps/validator";
import { DateTime } from "@bps/date-time";

export interface CreateSecretDialogProps {
  application: ApplicationDto;
  tenant: Tenant;
  onDismiss: (createSuccesful: boolean, secretText?: string) => void;
}

const secretFormValidator = new Validator<CloudApplicationSecret>(
  validator => ({
    startDateTime: validator.date().todayOrGreater(),
    endDateTime: [
      validator.date().todayOrGreater(),
      validator.custom().func<"endDateTime">((value, parentValues) => {
        if (
          DateTime.fromJSDate(value) >
          DateTime.fromJSDate(parentValues.startDateTime).plus({ years: 2 })
        ) {
          return "Cannot be greater than two years from start date.";
        } else return value;
      })
    ],
    displayName: validator.string().required()
  })
);

export const CreateSecretDialog: FunctionComponent<CreateSecretDialogProps> = ({
  application,
  tenant,
  onDismiss
}) => {
  const { mutateAsync: createApplicationSecret } = useCreateApplicationSecret();

  const onSubmit = async (formData: CloudApplicationSecret) => {
    const secretResponse = await createApplicationSecret({
      applicationId: application.id,
      tenantId: tenant.id,
      secret: formData
    });
    onDismiss(true, secretResponse.secretText);
  };

  const defaultValue = {
    displayName: "",
    startDateTime: new Date(),
    endDateTime: DateTime.now()
      .plus({ month: 12 })
      .toJSDate()
  };

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: "Create Secret",
        showCloseButton: true,
        onDismiss: () => onDismiss(false)
      }}
      minWidth={450}
    >
      <SubmissionForm<CloudApplicationSecret>
        onSubmit={onSubmit}
        initialValues={defaultValue}
        submitButtonProps={{ text: "Create" }}
        cancelButtonProps={{ onClick: () => onDismiss(false) }}
        validate={secretFormValidator.validate}
      >
        {() => (
          <CreateSecretForm
            application={application}
            tenant={tenant}
            onDismiss={() => onDismiss(false)}
          />
        )}
      </SubmissionForm>
    </Dialog>
  );
};
